import {
	CustomerEmissionControllerApiFactory,
	DataSourceDetailDataSourceTypeEnum,
	type DataSourceDetailSourceDescriptionEnum,
	type EmissionRequestProductFormData,
	type EmissionValueAnsweredUnit,
	EmissionValueGranularityLevel,
	GeographySourceType,
	MethodologiesImpactAssessmentMethodEnum,
	type ProductOrServiceCategory,
	type ReductionInitiative,
	TemplateControllerApiFactory,
	VerificationProvider,
} from '@api-client'
import { InfoIcon } from '@assets/icons'
import { InfoKeyValue } from '@components/InfoKeyValue'
import {
	BiogenicLandUseTooltip,
	BiogenicTooltip,
	ConfidenceTooltip,
	PcrTooltip,
} from '@components/Tooltips'
import { TotalSection } from '@components/TotalSection'
import { traverseAndSum } from '@helpers/arrays'
import { returnEFLocation } from '@helpers/componentValues'
import { formatCalendarDate } from '@helpers/dates'
import { getIsTransportationService } from '@helpers/productOrService'
import { useAsyncApi } from '@hooks/useAsyncApi'
import { useDropdownTranslations } from '@hooks/useDropdownTranslations'
import {
	Box,
	Chip,
	IconButton,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import * as S from './styles'

type Props = Partial<
	Pick<
		EmissionRequestProductFormData,
		| 'emissionValues'
		| 'dataSources'
		| 'methodologies'
		| 'verificationData'
		| 'reductionPlan'
		| 'reductionInitiatives'
	>
> & {
	unit?: EmissionValueAnsweredUnit
	productOrServiceCategory?: ProductOrServiceCategory
}

const ORDERED_DATA_SOURCE_TYPES = [
	DataSourceDetailDataSourceTypeEnum.PRIMARY,
	DataSourceDetailDataSourceTypeEnum.SECONDARY,
	DataSourceDetailDataSourceTypeEnum.BOTH,
	DataSourceDetailDataSourceTypeEnum.NONE,
] as const

/**
 * Downloads a file with the correct filename.
 *
 * We force download instead of opening in a new tab because:
 * - We use Bearer token authentication, so we can't open backend URLs directly
 * - Blob URLs always show UUIDs in the browser address bar and "Save As" dialog
 * - Forcing download ensures users get files with the correct filename immediately
 */
const downloadFile = (blob: Blob, filename: string) => {
	const file = new File([blob], filename, { type: blob.type })
	const fileURL = URL.createObjectURL(file)

	const downloadLink = document.createElement('a')
	downloadLink.href = fileURL
	downloadLink.download = filename
	downloadLink.style.display = 'none'
	document.body.append(downloadLink)
	downloadLink.click()
	document.body.removeChild(downloadLink)

	setTimeout(() => {
		URL.revokeObjectURL(fileURL)
	}, 100)
}

// TODO(larryhudson): Update reductionInitiatives status translation keys to the `reductionInitiatives.status_{key}` pattern so this can use i18n context.
const translateInitiativeStatus = (
	status: ReductionInitiative['status'],
	t: (key: string) => string
): string => {
	switch (status) {
		case 'PLANNED':
			return t('reductionInitiatives.status.planned')
		case 'IN_PROGRESS':
			return t('reductionInitiatives.status.inProgress')
		case 'COMPLETED':
			return t('reductionInitiatives.status.completed')
		case 'CANCELLED':
			return t('reductionInitiatives.status.cancelled')
		default:
			return '-'
	}
}

interface ReductionInitiativeItemProps {
	initiative: ReductionInitiative
	index: number
}

const ReductionInitiativeItem: React.FC<ReductionInitiativeItemProps> = ({
	initiative,
	index,
}) => {
	const { t } = useTranslation()
	return (
		<Stack gap={0.5}>
			<Typography variant="body2">
				{initiative.name || `Initiative ${index + 1}`}
			</Typography>
			{initiative.description && (
				<Typography variant="body2" color="text.secondary">
					{initiative.description}
				</Typography>
			)}
			<Stack direction="row" gap={1} flexWrap="wrap">
				{initiative.status && (
					<Chip
						label={translateInitiativeStatus(initiative.status, t)}
						size="small"
						variant="outlined"
					/>
				)}
				{initiative.startYear && (
					<Chip
						label={`${t('reductionInitiatives.startYear.question')}: ${initiative.startYear}`}
						size="small"
						variant="outlined"
					/>
				)}
				{initiative.endYear && (
					<Chip
						label={`${t('reductionInitiatives.endYear.question')}: ${initiative.endYear}`}
						size="small"
						variant="outlined"
					/>
				)}
				{initiative.lifecycleStagesImpacted?.map(stage => (
					<Chip
						key={stage}
						label={t('reductionInitiatives.lifecycleStagesImpacted.stage', {
							context: stage,
						})}
						size="small"
						variant="outlined"
					/>
				))}
			</Stack>
		</Stack>
	)
}

const ProductRequestDetails: React.FC<Props> = ({
	emissionValues,
	dataSources,
	methodologies,
	verificationData,
	reductionPlan,
	reductionInitiatives,
	unit,
	productOrServiceCategory,
}) => {
	const { templateId, requestId, productId } = useParams() as {
		templateId: string
		requestId: string
		productId: string
	}
	const customerEmissionControllerApi = useAsyncApi(
		CustomerEmissionControllerApiFactory
	)
	const templateControllerApi = useAsyncApi(TemplateControllerApiFactory)

	const { t } = useTranslation()
	const { translateDropdownOptions } = useDropdownTranslations()

	// Using translateDropdownOptions (rather than t() with dynamic context) to prevent
	// extract-translations from stripping the formField.geographySourceType_* context variant keys.
	const geographySourceTypeLabels = useMemo(
		() =>
			translateDropdownOptions('formField.geographySourceType', {
				context: Object.values(GeographySourceType),
			}),
		[translateDropdownOptions]
	)

	const isTransportationService = getIsTransportationService(
		productOrServiceCategory,
		unit
	)

	const productOrService = isTransportationService
		? t('word.service')
		: t('word.product')

	const translatedConfidenceLevel = t('emissions.confidence.name', {
		context: emissionValues?.confidence,
	})
	const translatedGranularityLevel = t('emissions.granularity.name', {
		context: `${emissionValues?.emissionValuesGranularity}${isTransportationService ? '_transportation' : ''}`,
	})
	const translatedBoundary = t('emissions.boundary.name', {
		context: emissionValues?.emissionValuesBoundary,
	})
	const translatedStandardUsed = t('standardsUsed.name', {
		context: methodologies?.methodologyStandards?.standardUsed,
	})
	const translatedAllocationMethodUsed = t(
		'formField.allocationMethodUsed.name',
		{ context: emissionValues?.allocationMethodUsed }
	)
	// translateDropdownOptions, not t() with dynamic context: extract-translations strips the
	// impactAssessmentMethod.name_* context variants otherwise.
	const impactAssessmentMethodLabels = useMemo(
		() =>
			translateDropdownOptions('impactAssessmentMethod.name', {
				context: Object.values(MethodologiesImpactAssessmentMethodEnum),
			}),
		[translateDropdownOptions]
	)
	const translatedImpactAssessmentMethod = (
		methodologies?.impactAssessmentMethod?.length
			? methodologies.impactAssessmentMethod
			: [MethodologiesImpactAssessmentMethodEnum.UNKNOWN]
	)
		.map(method => impactAssessmentMethodLabels[method])
		.join(', ')
	const translatedStandardCompliant = t(
		'emissionForm.methodologies.standardsCompliantOptions.answer',
		{ context: methodologies?.methodologyStandards?.standardCompliant }
	)
	const translateDataSourceDescription = (
		option: DataSourceDetailSourceDescriptionEnum
	): string => t('dataSources.description', { context: option })

	const orderedDataSources = useMemo(
		() =>
			(dataSources?.dataSourceDetails ?? []).toSorted(
				(a, b) =>
					ORDERED_DATA_SOURCE_TYPES.indexOf(a.dataSourceType) -
					ORDERED_DATA_SOURCE_TYPES.indexOf(b.dataSourceType)
			),
		[dataSources?.dataSourceDetails]
	)

	const onDownload = useCallback(
		async (attachmentId: string) => {
			// Find the attachment to get its name
			const allAttachments = [
				...(emissionValues?.emissionsAttachments || []),
				...(verificationData?.verificationAttachments || []),
			]
			const attachment = allAttachments.find(att => att.id === attachmentId)
			const filename = attachment?.name || 'download.pdf'

			const result = templateId
				? await templateControllerApi(api =>
						api.downloadTemplateAttachment(templateId, attachmentId, {
							responseType: 'blob',
						})
					)
				: await customerEmissionControllerApi(api =>
						api.downloadAttachment2(requestId, productId, attachmentId, {
							responseType: 'blob',
						})
					)
			downloadFile(result.data, filename)
		},
		[
			emissionValues?.emissionsAttachments,
			verificationData?.verificationAttachments,
			templateId,
			templateControllerApi,
			customerEmissionControllerApi,
			requestId,
			productId,
		]
	)

	const emissionsValuesBreakdownSubvalues = (): React.ReactNode => {
		const productUseValue =
			emissionValues?.emissionsValuesBreakdown?.productUse?.[0]?.value
		const endOfLifeValue =
			emissionValues?.emissionsValuesBreakdown?.endOfLife?.[0]?.value

		return (
			(emissionValues?.emissionValuesGranularity ===
				EmissionValueGranularityLevel.DETAILED_LIFECYCLE ||
				emissionValues?.emissionValuesGranularity ===
					EmissionValueGranularityLevel.LIFECYCLE) && (
				<>
					<InfoKeyValue
						className="key-value-container"
						label={t('emissions.lifecycle.title', {
							context: 'materialAcquisitionAndPreprocessing',
						})}
						value={traverseAndSum(
							emissionValues?.emissionsValuesBreakdown
								?.materialAcquisitionAndPreprocessing || [],
							'value'
						)}
					/>
					<InfoKeyValue
						className="key-value-container"
						label={t('emissions.lifecycle.title', {
							context: 'production',
						})}
						value={traverseAndSum(
							emissionValues?.emissionsValuesBreakdown?.production || [],
							'value'
						)}
					/>
					<InfoKeyValue
						className="key-value-container"
						label={t('emissions.lifecycle.title', {
							context: 'distributionAndStorage',
						})}
						value={traverseAndSum(
							emissionValues?.emissionsValuesBreakdown
								?.distributionAndStorage || [],
							'value'
						)}
					/>
					{productUseValue !== undefined && productUseValue !== 0 && (
						<InfoKeyValue
							className="key-value-container"
							label={t('emissions.lifecycle.title', {
								context: 'productUse',
							})}
							value={traverseAndSum(
								emissionValues?.emissionsValuesBreakdown?.productUse || [],
								'value'
							)}
						/>
					)}
					{endOfLifeValue !== undefined && endOfLifeValue !== 0 && (
						<InfoKeyValue
							className="key-value-container"
							label={t('emissions.lifecycle.title', {
								context: 'endOfLife',
							})}
							value={traverseAndSum(
								emissionValues?.emissionsValuesBreakdown?.endOfLife || [],
								'value'
							)}
						/>
					)}
				</>
			)
		)
	}
	const renewableEnergyDetails = emissionValues?.hasRenewableEnergyAtSite
		? [
				emissionValues?.percentageRenewableEnergyOnPPAs !== undefined &&
					t('percentageDisplay', {
						name: t('formField.percentageRenewableEnergyOnPPAs'),
						percentage: emissionValues.percentageRenewableEnergyOnPPAs,
					}),
				emissionValues?.percentageRenewableEnergyOnSite !== undefined &&
					t('percentageDisplay', {
						name: t('formField.percentageRenewableEnergyOnSite'),
						percentage: emissionValues.percentageRenewableEnergyOnSite,
					}),
				emissionValues?.percentageRenewableOnGreenTariff !== undefined &&
					t('percentageDisplay', {
						name: t('formField.percentageRenewableOnGreenTariff'),
						percentage: emissionValues.percentageRenewableOnGreenTariff,
					}),
				emissionValues?.percentageRenewableEnergyOnUnbundledRECs !==
					undefined &&
					t('percentageDisplay', {
						name: t('formField.percentageRenewableEnergyOnUnbundledRECs'),
						percentage: emissionValues.percentageRenewableEnergyOnUnbundledRECs,
					}),
			]
				.filter(Boolean)
				.join(', ')
		: ''

	const renewableEnergyAtSiteAnswer =
		t('hasRenewableEnergyAtSite.answer', {
			context: emissionValues?.hasRenewableEnergyAtSite?.toString(),
		}) || '-'
	const renewableEnergyAtSite = renewableEnergyDetails
		? t('displayWithDetails', {
				content: renewableEnergyAtSiteAnswer,
				details: renewableEnergyDetails,
			})
		: renewableEnergyAtSiteAnswer
	const verificationDataDetails = verificationData?.isProductVerified
		? verificationData?.isToolVerified
			? t('formField.verificationToolOrProduct.both')
			: t('formField.verificationToolOrProduct.product')
		: verificationData?.isToolVerified
			? t('formField.verificationToolOrProduct.tool')
			: ''
	return (
		<Box sx={{ display: 'flex', gap: 2 }}>
			<Box sx={{ flexGrow: 10 }}>
				<S.DetailsContainer>
					{!emissionValues && !dataSources && !methodologies && (
						<Typography component="h4">
							{t('emissionForm.noDataPlaceHolder')}
						</Typography>
					)}
					{emissionValues && (
						<S.DetailsSubContainer>
							<S.DetailsTitle>
								{t('emissionForm.emissionValues')}
							</S.DetailsTitle>
							<InfoKeyValue
								className="key-value-container"
								label={t('formField.referenceYear')}
								value={emissionValues.referenceYear || '-'}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('formField.scope1Emissions')}
								value={emissionValues.scope1Emissions || '-'}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('formField.scope2Emissions')}
								value={emissionValues.scope2Emissions || '-'}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.hasRenewableEnergyAtSite')}
								value={renewableEnergyAtSite}
							/>

							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.granularity.title')}
								value={translatedGranularityLevel}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.boundary.title')}
								value={translatedBoundary}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.hasAppliedExemptions', {
									context: isTransportationService
										? 'transportation'
										: 'product',
								})}
								value={`${t('emissions.hasAppliedExemptions.answer', {
									context: emissionValues.hasAppliedExemptions?.toString(),
								})}${
									emissionValues.appliedExemptionsComments
										? ' (' + emissionValues.appliedExemptionsComments + ')'
										: ''
								}`}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.isUsingMassBalancing')}
								value={t('emissions.hasAppliedExemptions.answer', {
									context: emissionValues.isUsingMassBalancing?.toString(),
								})}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('formField.hasAllocationBetweenCoProducts.label')}
								value={t('formField.hasAllocationBetweenCoProducts.answer', {
									context:
										emissionValues.hasAllocationBetweenCoProducts?.toString(),
								})}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.allocationMethodUsedLabel')}
								value={
									emissionValues.allocationMethodUsed
										? translatedAllocationMethodUsed
										: '-'
								}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.isExactProductionProcess')}
								value={`${
									emissionValues.isExactProductionProcess
										? t('formField.isExactProductionProcess', {
												context: emissionValues.isExactProductionProcess,
											})
										: '-'
								}${
									emissionValues.exactProductionProcessComments
										? ' (' + emissionValues.exactProductionProcessComments + ')'
										: ''
								}`}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.emissionsTotalValue')}
								value={emissionValues.emissionsValuesBreakdown?.value}
							/>
							{emissionsValuesBreakdownSubvalues()}
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.emissionsUnit')}
								value={t('emissions.unit.name', {
									context: unit,
								})}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.emissionsAttachments')}
								value={emissionValues.emissionsAttachments || '-'}
								action={onDownload}
							/>
							{emissionValues.includeDownstreamTransportationEmissions !==
								undefined &&
								emissionValues.includeDownstreamTransportationEmissions !==
									null && (
									<>
										<InfoKeyValue
											className="key-value-container"
											label={t(
												'emissionForm.includeDownstreamTransportationEmissions.label'
											)}
											value={t(
												'emissionForm.includeDownstreamTransportationEmissions.answer',
												{
													context:
														emissionValues.includeDownstreamTransportationEmissions.toString(),
												}
											)}
										/>
										<InfoKeyValue
											className="key-value-container"
											label={t(
												'emissionForm.downstreamTransportationGhgEmissions'
											)}
											value={
												emissionValues.downstreamTransportationGhgEmissions
													? `${emissionValues.downstreamTransportationGhgEmissions} %`
													: '-'
											}
										/>
									</>
								)}
							{emissionValues?.includePackagingEmissions !== undefined &&
								emissionValues?.includePackagingEmissions !== null && (
									<>
										<InfoKeyValue
											className="key-value-container"
											label={t('emissionForm.includePackagingEmissions.label')}
											value={t(
												'emissionForm.includePackagingEmissions.answer',
												{
													context:
														emissionValues.includePackagingEmissions.toString(),
												}
											)}
										/>
										<InfoKeyValue
											className="key-value-container"
											label={t('emissionForm.packagingGhgEmissions')}
											value={
												emissionValues.packagingGhgEmissions
													? emissionValues.packagingGhgEmissions
													: '-'
											}
										/>
									</>
								)}
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.percentageOfPcr')}
								value={
									emissionValues.percentageOfPcrInPackaging
										? `${emissionValues.percentageOfPcrInPackaging} %`
										: '-'
								}
							>
								<Tooltip title={<PcrTooltip />}>
									<IconButton>
										<InfoIcon />
									</IconButton>
								</Tooltip>
							</InfoKeyValue>
							{emissionValues.includeBiogenicEmissions !== undefined && (
								<>
									<InfoKeyValue
										className="key-value-container"
										label={t('emissionForm.totalBiogenicEmissions')}
										value={emissionValues.biogenicEmissionsValue}
									>
										<Tooltip title={<BiogenicTooltip />}>
											<IconButton>
												<InfoIcon />
											</IconButton>
										</Tooltip>
									</InfoKeyValue>
									<InfoKeyValue
										className="key-value-container"
										label={t('emissionForm.landUseChangeBiogenicEmissions')}
										value={emissionValues.biogenicLandUseEmissionsValue}
									>
										<Tooltip title={<BiogenicLandUseTooltip />}>
											<IconButton>
												<InfoIcon />
											</IconButton>
										</Tooltip>
									</InfoKeyValue>

									<InfoKeyValue
										className="key-value-container"
										label={t('emissionForm.otherBiogenicEmissions')}
										value={emissionValues.otherBiogenicEmissionsValue}
									/>
								</>
							)}
							<>
								{emissionValues.emissionsValuesBreakdown
									?.valueExcludingBiogenicCarbon !== undefined && (
									<InfoKeyValue
										className="key-value-container"
										label={t('emissionForm.totalPcf.fieldLabel')}
										value={
											emissionValues.emissionsValuesBreakdown
												.valueExcludingBiogenicCarbon
										}
									/>
								)}
								{emissionValues.emissionsValuesBreakdown
									?.valueExcludingBiogenicCarbon === undefined &&
									emissionValues.emissionsValuesBreakdown?.value !==
										undefined && (
										<InfoKeyValue
											className="key-value-container"
											label={t('formField.valueRequired')}
											value={emissionValues.emissionsValuesBreakdown.value}
										/>
									)}

								{emissionValues.percentageFromOwnOperations !== undefined && (
									<InfoKeyValue
										className="key-value-container"
										label={t('emissionForm.percentageFromOwnOperations.label')}
										value={
											emissionValues.percentageFromOwnOperations
												? `${emissionValues.percentageFromOwnOperations} %`
												: '-'
										}
									/>
								)}
								<InfoKeyValue
									className="key-value-container"
									label={t(
										'emissionForm.biogenicEmissionsBreakdown.biogenicNonCO2'
									)}
									value={emissionValues.biogenicNonCO2EmissionsValue ?? '-'}
								>
									<Tooltip
										title={t(
											'tooltip.biogenicEmissionsBreakdown.biogenicNonCO2'
										)}
									>
										<IconButton>
											<InfoIcon />
										</IconButton>
									</Tooltip>
								</InfoKeyValue>
								<InfoKeyValue
									className="key-value-container"
									label={t('emissionForm.biogenicEmissionsBreakdown.fossil')}
									value={emissionValues.fossilEmissionsValue ?? '-'}
								>
									<Tooltip
										title={t('tooltip.biogenicEmissionsBreakdown.fossil')}
									>
										<IconButton>
											<InfoIcon />
										</IconButton>
									</Tooltip>
								</InfoKeyValue>
								<InfoKeyValue
									className="key-value-container"
									label={t(
										'emissionForm.biogenicEmissionsBreakdown.landUseChange'
									)}
									value={emissionValues.biogenicLandUseEmissionsValue ?? '-'}
								>
									<Tooltip
										title={t(
											'tooltip.biogenicEmissionsBreakdown.landUseChange'
										)}
									>
										<IconButton>
											<InfoIcon />
										</IconButton>
									</Tooltip>
								</InfoKeyValue>
								{emissionValues.includeBiogenicCO2Uptake !== undefined &&
									emissionValues.includeBiogenicCO2Uptake !== null && (
										<>
											<InfoKeyValue
												className="key-value-container"
												label={t('emissionForm.includeBiogenicCO2Uptake.label')}
												value={t(
													'emissionForm.includeBiogenicCO2Uptake.answer',
													{
														context:
															emissionValues?.includeBiogenicCO2Uptake?.toString(),
													}
												)}
											>
												<Tooltip title={t('tooltip.biogenicCO2Uptake')}>
													<IconButton>
														<InfoIcon />
													</IconButton>
												</Tooltip>
											</InfoKeyValue>
											{emissionValues.includeBiogenicCO2Uptake && (
												<InfoKeyValue
													className="key-value-container"
													label={t(
														'emissionForm.emissionsWithBiogenicValue.label'
													)}
													value={
														emissionValues.emissionsWithBiogenicValue ??
														(emissionValues.biogenicCO2UptakeValue
															? Math.abs(emissionValues.biogenicCO2UptakeValue)
															: undefined)
													}
												/>
											)}
										</>
									)}
							</>

							{emissionValues?.emissionValuesLocation?.geographySourceType && (
								<InfoKeyValue
									className="key-value-container"
									label={t('formField.geographySourceType')}
									value={
										geographySourceTypeLabels[
											emissionValues.emissionValuesLocation.geographySourceType
										]
									}
								/>
							)}
							{emissionValues?.emissionValuesLocation?.geographySiteDetail && (
								<InfoKeyValue
									className="key-value-container"
									label={t('formField.geographySiteDetail')}
									value={
										emissionValues.emissionValuesLocation.geographySiteDetail
									}
								/>
							)}
							<InfoKeyValue
								className="key-value-container"
								label={t('table.location')}
								value={returnEFLocation(
									t,
									emissionValues?.emissionValuesLocation
								)}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('table.manufacturingLocation')}
								value={returnEFLocation(
									t,
									emissionValues?.emissionValuesLocation
										?.isGeographyMatchingManufacturing
										? emissionValues?.emissionValuesLocation
										: emissionValues?.emissionValuesLocation
												?.manufacturingLocation
								)}
							/>

							{emissionValues?.confidence && (
								<InfoKeyValue
									className="key-value-container"
									label={t('emissionForm.confidence.title')}
									value={translatedConfidenceLevel}
								>
									<Tooltip title={<ConfidenceTooltip />}>
										<IconButton>
											<InfoIcon />
										</IconButton>
									</Tooltip>
								</InfoKeyValue>
							)}
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.additionalInformation')}
								value={emissionValues.additionalInformation || '-'}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.pcfMainContributorsComment')}
								value={emissionValues.pcfMainContributorsComment || '-'}
							/>
						</S.DetailsSubContainer>
					)}
					{dataSources && (
						<S.DetailsSubContainer>
							<S.DetailsTitle>
								{t('emissionForm.dataSources.title')}
							</S.DetailsTitle>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.dataSources.shareOfPrimaryData')}
								value={
									dataSources.shareOfPrimaryDataInEmissionFactor !==
										undefined &&
									dataSources.shareOfPrimaryDataInEmissionFactor !== null
										? `${dataSources.shareOfPrimaryDataInEmissionFactor}%`
										: t('unknown')
								}
							/>
							{orderedDataSources.map((dataSourceDetail, index) => (
								<div key={(dataSourceDetail.dataSourceType || '-') + index}>
									<InfoKeyValue
										className="key-value-container"
										label={t('dataSources.sourceNumber', {
											number: index + 1,
										})}
										value={dataSourceDetail.dataSourceType}
									/>
									<InfoKeyValue
										className="key-value-container"
										label={t('emissionForm.dataSources.dataSourceDescription')}
										value={
											dataSourceDetail.sourceDescription
												? translateDataSourceDescription(
														dataSourceDetail.sourceDescription
													)
												: '-'
										}
									/>
									<InfoKeyValue
										className="key-value-container"
										label={t('emissionForm.dataSources.additionalSourceInfo')}
										value={dataSourceDetail.sourceDescriptionOther || '-'}
									/>
									{dataSources.dataSourceDetails?.length !== index + 1 && (
										<S.DetailsSpacer />
									)}
								</div>
							))}
						</S.DetailsSubContainer>
					)}
					{methodologies && (
						<S.DetailsSubContainer>
							<S.DetailsTitle>
								{t('emissionForm.methodologies.title')}
							</S.DetailsTitle>
							<InfoKeyValue
								className="key-value-container"
								label={t(
									'emissionForm.methodologies.standardCompliantGhgScope3Protocol'
								)}
								value={translatedStandardCompliant}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.methodologies.standards')}
								value={
									methodologies?.methodologyStandards?.standardUsed
										? translatedStandardUsed
										: '-'
								}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.methodologies.impactAssessment')}
								value={translatedImpactAssessmentMethod}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.methodologies.additionalStandardInfo')}
								value={
									methodologies?.methodologyStandards?.additionalInformation ||
									'-'
								}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.methodologies.dataVerification')}
								value={
									t('emissionForm.methodologies.isVerifiedOptions.answer', {
										context: verificationData?.isDataVerified
											? 'true'
											: 'false',
									}) +
									(verificationDataDetails
										? `  - ${verificationDataDetails})`
										: '')
								}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.methodologies.calculatorUsed')}
								value={
									emissionValues?.emissionValuesGranularity ===
									EmissionValueGranularityLevel.CALCULATOR
										? t('yes')
										: t('no')
								}
							/>

							{verificationData?.isDataVerified ? (
								<>
									{verificationData?.verificationProvider !==
									VerificationProvider.OTHER ? (
										<InfoKeyValue
											className="key-value-container"
											label={t('emissionForm.methodologies.dataVerifiedBy')}
											value={
												verificationData?.verificationProvider
													? t('verificationProvider.name', {
															context: verificationData.verificationProvider,
														})
													: '-'
											}
										/>
									) : null}
									<InfoKeyValue
										className="key-value-container"
										label={
											verificationData?.verificationProvider ===
											VerificationProvider.OTHER
												? t('emissionForm.methodologies.dataVerifiedBy')
												: t(
														'emissionForm.methodologies.verificationProviderDetails'
													)
										}
										value={
											verificationData?.additionalVerificationProviderDetails ||
											'-'
										}
									/>
									<InfoKeyValue
										className="key-value-container"
										label={t(
											'emissionForm.methodologies.verificationAccordingToStandard'
										)}
										value={
											verificationData?.verificationStandard
												? t('standardsUsed.name', {
														context: verificationData?.verificationStandard,
													})
												: '-'
										}
									/>
									<InfoKeyValue
										className="key-value-container"
										label={t(
											'emissionForm.methodologies.verificationStandardDetails'
										)}
										value={
											verificationData?.additionalVerificationStandardDetails ||
											'-'
										}
									/>

									<InfoKeyValue
										className="key-value-container"
										label={t('emissionForm.methodologies.verificationPDF')}
										value={verificationData?.verificationAttachments || '-'}
										action={onDownload}
									/>
								</>
							) : null}
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.methodologies.referencePeriod')}
								value={
									methodologies.collectionStartDate &&
									methodologies.collectionEndDate
										? formatCalendarDate(methodologies.collectionStartDate) +
											' - ' +
											formatCalendarDate(methodologies.collectionEndDate)
										: '-'
								}
							/>
							<InfoKeyValue
								className="key-value-container"
								label={t('emissionForm.methodologies.validityPeriod')}
								value={
									methodologies.validityStartDate &&
									methodologies.validityEndDate
										? formatCalendarDate(methodologies.validityStartDate) +
											' - ' +
											formatCalendarDate(methodologies.validityEndDate)
										: '-'
								}
							/>
							{/* Checking undefined for backward compatibility */}
							{reductionPlan &&
								reductionPlan?.hasReductionPlan !== undefined && (
									<>
										<Stack gap={1}>
											<InfoKeyValue
												className="key-value-container"
												label={t(
													'emissionForm.reductionPlan.hasReductionPlan',
													{
														productOrService,
													}
												)}
												value={`${t('emissionForm.reductionPlan.answer', {
													context: reductionPlan?.hasReductionPlan
														? 'true'
														: 'false',
												})}${
													reductionPlan?.hasReductionPlan &&
													reductionPlan.reductionPlanComments
														? ` (${reductionPlan.reductionPlanComments})`
														: ''
												}`}
											/>
											{reductionPlan.hasReductionPlan &&
												reductionInitiatives &&
												reductionInitiatives.length > 0 && (
													<Stack gap={2}>
														{reductionInitiatives.map((initiative, index) => (
															<ReductionInitiativeItem
																key={index}
																initiative={initiative}
																index={index}
															/>
														))}
													</Stack>
												)}
										</Stack>
										<InfoKeyValue
											className="key-value-container"
											label={t(
												'emissionForm.reductionPlan.hasAlternativeWithLowerCarbon',
												{ productOrService }
											)}
											value={`${t(
												'emissionForm.reductionPlan.alternativeWithLowerCarbon.answer',
												{
													context: reductionPlan?.hasAlternativeWithLowerCarbon
														? 'true'
														: 'false',
												}
											)}${
												reductionPlan.hasAlternativeWithLowerCarbonComments
													? ` (${reductionPlan.hasAlternativeWithLowerCarbonComments})`
													: ''
											}`}
										/>
									</>
								)}
						</S.DetailsSubContainer>
					)}
				</S.DetailsContainer>
			</Box>
			{emissionValues?.emissionValuesGranularity &&
				emissionValues?.emissionsValuesBreakdown && (
					<TotalSection
						viewType="customer"
						emissionValuesGranularity={
							emissionValues?.emissionValuesGranularity
						}
						emissionsValuesBreakdown={emissionValues?.emissionsValuesBreakdown}
						unit={unit}
						productOrServiceCategory={productOrServiceCategory}
					/>
				)}
		</Box>
	)
}

export { ProductRequestDetails }
